import { computed } from 'mobx';

import { concatPath } from '@feathr/hooks';
import type { IBaseAttributes, TConstraints } from '@feathr/rachis';
import { Collection, DisplayModel, isWretchError, wretch } from '@feathr/rachis';

/**
 * State of the Meta integration, derived by the backend from setup completion,
 * business manager token health, page sync state, and active page availability.
 */
export type TMetaIntegrationStatus =
  /**
   * Setup is in progress. Applies in three scenarios:
   * - Pages have not yet synced (waiting on async sync job).
   * - Pages have synced and active pages exist, but child business manager setup
   *   is still finishing.
   * - Setup is complete but a reconnect is underway (business manager token is
   *   being regenerated).
   */
  | 'configuring'
  /** Setup is complete, the business manager token is healthy, and at least one active page exists. */
  | 'connected'
  /** Child business manager setup failed; a `setup_error` is recorded on the integration. */
  | 'error'
  /**
   * Pages have synced but no eligible active pages are available. Applies either
   * when the initial sync returns no pages, or when a fully set up integration
   * has had all of its pages removed or archived.
   */
  | 'no_active_pages'
  /** Integration is archived or has never been connected. */
  | 'not_connected';

/** OAuth config returned by the backend */
export interface IMetaOAuthConfig {
  app_id: string;
  api_version: string;
  redirect_uri: string;
  config_id: string;
}

/** Individual Facebook or Instagram page */
export interface IMetaPage {
  id: string;
  page_id: string;
  name: string;
  category?: string;
  fan_count?: number;
  fb_business_id?: string;
  fb_business_name?: string;
  instagram_account_id?: string;
  instagram_username?: string;
}

/** User who initiated the Meta connection */
export interface IMetaConnectedBy {
  id: string;
  email: string;
  name: string;
}

/** First connection record for a given Feathr user. */
export interface IMetaLastUserConnection {
  id: string;
  /** Date the user first initiated the integration. Does not update on reconnection. */
  connected_at: string;
  is_token_expired: boolean;
  connected_by: IMetaConnectedBy;
}

/** Attributes for Meta integration model */
export interface IMetaIntegrationAttributes extends IBaseAttributes {
  /** Link to Feathr account (unique - only one MetaAccount per Feathr account) */
  account_id: string;
  status: TMetaIntegrationStatus;

  /** Whether setup is complete and the business manager token is valid. */
  is_healthy: boolean;
  type: 'MetaAccount';
  oauth_config?: IMetaOAuthConfig;
  last_user_connection?: IMetaLastUserConnection;

  /** Account setup tracking fields */
  setup_completed_at?: string;
  pages_synced_at?: string;
  setup_error?: string;
}

/** Response from POST /integrations/meta/disconnect */
export interface IMetaDisconnectResponse {
  status: 'disconnected';
  connections_deactivated: number;
}

/** Response from POST /integrations/meta/pages/sync */
export interface IMetaSyncResponse {
  status: 'sync_queued';
}

/** Response from GET /integrations/meta/og_tags */
export interface IMetaOgTags {
  headline: string;
  description: string;
  display_link: string;
  image: string;
}

export class MetaIntegration extends DisplayModel<IMetaIntegrationAttributes> {
  public readonly className = 'MetaIntegration';

  public get constraints(): TConstraints<IMetaIntegrationAttributes> {
    return {
      status: {
        presence: true,
        inclusion: ['not_connected', 'configuring', 'connected', 'error', 'no_active_pages'],
      },
    };
  }

  public getItemUrl(pathSuffix?: string): string {
    return concatPath('integrations/meta', pathSuffix);
  }

  public get name(): string {
    return 'Meta Integration';
  }

  /** Raw backend-derived status; `undefined` only on uninitialized instances. */
  @computed
  public get status(): TMetaIntegrationStatus | undefined {
    return this.get('status');
  }

  @computed
  public get isConnected(): boolean {
    return this.get('status') === 'connected';
  }

  @computed
  public get isConfiguring(): boolean {
    return this.get('status') === 'configuring';
  }

  @computed
  public get isNotConnected(): boolean {
    return this.get('status') === 'not_connected';
  }

  @computed
  public get isError(): boolean {
    return this.get('status') === 'error';
  }

  @computed
  public get isNoActivePages(): boolean {
    return this.get('status') === 'no_active_pages';
  }

  /** Human-readable error message persisted by the backend when setup fails. */
  @computed
  public get setupError(): string | undefined {
    return this.get('setup_error');
  }

  /**
   * ISO timestamp marking when initial setup (child BM + BM token) first
   * completed. Used to distinguish initial-setup `configuring` from reconnect
   * `configuring` in the UI.
   */
  @computed
  public get setupCompletedAt(): string | undefined {
    return this.get('setup_completed_at');
  }

  @computed
  public get oauthConfig(): IMetaOAuthConfig | undefined {
    return this.get('oauth_config');
  }

  @computed
  public get lastUserConnection(): IMetaLastUserConnection | undefined {
    return this.get('last_user_connection');
  }

  /** Date the user first connected. Does not update on reconnection. */
  @computed
  public get connectedAt(): string | undefined {
    return this.lastUserConnection?.connected_at;
  }

  @computed
  public get connectedBy(): IMetaConnectedBy | undefined {
    return this.lastUserConnection?.connected_by;
  }

  @computed
  public get isTokenExpired(): boolean {
    return this.lastUserConnection?.is_token_expired ?? false;
  }

  @computed
  public get isHealthy(): boolean {
    return this.get('is_healthy') ?? false;
  }

  /**
   * Get Facebook pages connected to this integration.
   * GET /integrations/meta/pages
   */
  public async getPages(): Promise<IMetaPage[]> {
    if (!this.isConnected && !this.isConfiguring) {
      return [];
    }

    this.assertCollection(this.collection);

    const url = `${this.collection.getHostname()}integrations/meta/pages`;
    const response = await wretch<IMetaPage[] & Record<string, unknown>>(url, {
      headers: this.collection.getHeaders(),
      method: 'GET',
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  /**
   * Disconnect the Meta integration.
   * POST /integrations/meta/disconnect
   */
  public async disconnect(): Promise<IMetaDisconnectResponse> {
    this.assertCollection(this.collection);

    const url = `${this.collection.getHostname()}integrations/meta/disconnect`;
    const response = await wretch<IMetaDisconnectResponse & Record<string, unknown>>(url, {
      headers: this.collection.getHeaders(),
      method: 'POST',
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    this.set({
      status: 'not_connected',
      last_user_connection: undefined,
      is_healthy: false,
      // Clear setup-tracking fields so a stale setup_error from a prior
      // failed attempt can't flash on the next reconnect before the backend
      // response lands.
      setup_error: undefined,
      setup_completed_at: undefined,
      pages_synced_at: undefined,
    } as Partial<IMetaIntegrationAttributes>);

    return response.data;
  }

  /**
   * Trigger a manual Facebook page sync from the Meta API.
   * POST /integrations/meta/pages/sync
   */
  public async syncPages(): Promise<IMetaSyncResponse> {
    this.assertCollection(this.collection);

    const url = `${this.collection.getHostname()}integrations/meta/pages/sync`;
    const response = await wretch<IMetaSyncResponse & Record<string, unknown>>(url, {
      headers: this.collection.getHeaders(),
      method: 'POST',
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  /**
   * Build the OAuth URL to initiate Meta connection.
   * Requires oauth_config to be present.
   */
  public getOAuthUrl(): string | null {
    const config = this.oauthConfig;
    if (!config) {
      return null;
    }

    const { app_id: appId, api_version: apiVersion, redirect_uri: redirectUri } = config;
    const oauthUrl = new URL(`https://www.facebook.com/${apiVersion}/dialog/oauth`);
    oauthUrl.searchParams.set('client_id', appId);
    oauthUrl.searchParams.set('redirect_uri', redirectUri);
    oauthUrl.searchParams.set('config_id', config.config_id);
    oauthUrl.searchParams.set('response_type', 'code');

    return oauthUrl.toString();
  }
}

export class MetaIntegrations extends Collection<MetaIntegration> {
  public getModel(attributes: IMetaIntegrationAttributes): MetaIntegration {
    return new MetaIntegration(attributes);
  }

  public getClassName(): string {
    return 'meta_integration';
  }

  public override url(): string {
    return `${this.getHostname()}integrations/meta/account`;
  }

  /**
   * Fetch OG tags for a destination URL.
   * GET /integrations/meta/og_tags?url=<url>
   *
   * @param url - The destination URL to fetch OG tags for.
   * @returns Parsed OG tag data from the backend.
   */
  public async getOgTags(url: string): Promise<IMetaOgTags> {
    const endpoint = `${this.getHostname()}integrations/meta/og_tags?url=${encodeURIComponent(url)}`;
    const response = await wretch<IMetaOgTags & Record<string, unknown>>(endpoint, {
      headers: this.getHeaders(),
      method: 'GET',
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  /**
   * Fetch the Meta integration status.
   * Unlike other collections, Meta has a single integration per account,
   * so we fetch from /account endpoint directly.
   */
  public async fetchIntegration(): Promise<MetaIntegration> {
    const response = await wretch<IMetaIntegrationAttributes>(this.url(), {
      headers: this.getHeaders(),
      method: 'GET',
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return this.processJSONResponse(response);
  }
}
